/**
 * SwipeHintPill — reusable first-run "← Swipe →" hint for any swipable surface.
 *
 * Drop it inside a `position: relative` wrapper next to a horizontal-snap
 * scroller and it will:
 *   - Render an amber-glow pill in the bottom-right corner (pointer-events
 *     none, so taps pass straight through to the cards underneath).
 *   - Auto-fade out after `autoHideMs` (default 6s).
 *   - Auto-fade out the first time the parent calls `ref.current.dismiss()`
 *     (wire this up to your onScroll handler so the hint vanishes the
 *     instant the user discovers swipe).
 *   - Persist dismissal in localStorage under `swipeHint:{storageKey}` so
 *     returning users never see it again.
 *   - Hide entirely at the caller-supplied `hideAt` breakpoint (default
 *     "sm:hidden") — typically that's the breakpoint where the carousel
 *     becomes a grid and a swipe hint no longer applies.
 *
 * Usage:
 *   const hintRef = useRef(null);
 *   const onScroll = () => { hintRef.current?.dismiss(); ... }
 *   return (
 *     <div className="relative">
 *       <div onScroll={onScroll} className="flex overflow-x-auto snap-x">
 *         {items}
 *       </div>
 *       <SwipeHintPill ref={hintRef} storageKey="my-carousel" hideAt="xl:hidden" />
 *     </div>
 *   );
 */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";

const SwipeHintPill = forwardRef(function SwipeHintPill(
  {
    storageKey,
    hideAt = "sm:hidden",
    label = "Swipe",
    autoHideMs = 6000,
    testId,
  },
  ref
) {
  const lsKey = `swipeHint:${storageKey}`;
  const [visible, setVisible] = useState(() => {
    if (!storageKey) return false;
    try { return localStorage.getItem(lsKey) !== "1"; } catch { return true; }
  });

  const dismiss = () => {
    setVisible(false);
    try { if (storageKey) localStorage.setItem(lsKey, "1"); } catch { /* private mode */ }
  };

  useImperativeHandle(ref, () => ({ dismiss, isVisible: () => visible }), [visible]);

  // Auto-dismiss after autoHideMs even if the user never interacts —
  // keeps the pill from feeling like permanent UI clutter.
  useEffect(() => {
    if (!visible) return undefined;
    const t = setTimeout(dismiss, autoHideMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, autoHideMs]);

  if (!storageKey) return null;

  return (
    <div
      className={`pointer-events-none absolute right-3 bottom-3 ${hideAt} transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
      aria-hidden="true"
      data-testid={testId || `swipe-hint-${storageKey}`}
    >
      <div className="flex items-center gap-1.5 bg-black/70 backdrop-blur-md border border-white/15 rounded-full px-3 py-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.45)]">
        <span
          className="text-amber-300 text-[11px] font-black tracking-[0.15em] uppercase"
          style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
        >
          <span className="inline-block animate-pulse">←</span> {label} <span className="inline-block animate-pulse">→</span>
        </span>
      </div>
    </div>
  );
});

export default SwipeHintPill;
